import { bidBoardAuthHeaders, signedInBidUserId } from './bidBoardAuth';
import type { SiteSharingDuration } from '../../shared/operatorSiteSharing';
export type { SiteSharingDuration } from '../../shared/operatorSiteSharing';
export const SITE_SHARING_CHANGED='zia:operator-site-sharing-changed';
export type SiteSharing = {enabled:boolean;consentId:string|null;expiresAt:string|null;duration?:SiteSharingDuration;onceUsed?:boolean;backgroundAllowed?:boolean};
export type SiteFormActivity={key:string;title:string;siteKey?:string|null;apiNumber?:string|null};
const activeForms=new Map<string,{user:string;form:SiteFormActivity}>();
export const setSiteFormActivity=(owner:string,form:SiteFormActivity|null)=>{if(form)activeForms.set(owner,{user:signedInBidUserId(),form});else activeForms.delete(owner);};
const key=(user:string)=>`zia:operator-site-sharing:${user}`;
const sessionKey=(user:string)=>`${key(user)}:session`;
async function request<T>(method='GET',body?:unknown,keepalive=false):Promise<T> {
  const controller=new AbortController();
  const timeout=window.setTimeout(()=>controller.abort(),12000);
  try {
    const response=await fetch('/api/operator-site-presence',{method,signal:controller.signal,headers:{...bidBoardAuthHeaders(),'Content-Type':'application/json'},cache:'no-store',keepalive,
      ...(body===undefined?{}:{body:JSON.stringify(body)})});
    const data=await response.json();
    if(!response.ok)throw new Error(data.error || 'Site location sharing is unavailable. Retry.');
    return data as T;
  } finally {window.clearTimeout(timeout);}
}
export const getSiteSharing=()=>request<SiteSharing>();
export function siteSharingOnDevice(sharing:SiteSharing,user=signedInBidUserId()) {
  try {return !!user && sharing.enabled && !sharing.onceUsed && Date.parse(sharing.expiresAt||'')>Date.now() && !!sharing.consentId
    && localStorage.getItem(key(user))===sharing.consentId
    && (sharing.duration!=='session'||sessionStorage.getItem(sessionKey(user))===sharing.consentId);} catch {return false;}
}
export async function setSiteSharing(enabled:boolean,duration:SiteSharingDuration='12h') {
  const user=signedInBidUserId();
  if(!user)throw new Error('Sign in to share with the site operator.');
  if(!enabled){localStorage.removeItem(key(user));sessionStorage.removeItem(sessionKey(user));window.dispatchEvent(new Event(SITE_SHARING_CHANGED));}
  const result=await request<SiteSharing>('PATCH',{enabled,duration,policyVersion:2});
  if(enabled && result.consentId && user===signedInBidUserId()) {
    localStorage.setItem(key(user),result.consentId);
    if(duration==='session')sessionStorage.setItem(sessionKey(user),result.consentId);
    else sessionStorage.removeItem(sessionKey(user));
  }
  window.dispatchEvent(new Event(SITE_SHARING_CHANGED));
  return result;
}
/** Session consent ends on hide/close; a delayed stop cannot cancel a replacement grant. */
export async function endSiteSharingSession() {
  const user=signedInBidUserId();
  if(!user)return;
  const consentId=sessionStorage.getItem(sessionKey(user));
  if(!consentId)return;
  sessionStorage.removeItem(sessionKey(user));
  if(localStorage.getItem(key(user))===consentId)localStorage.removeItem(key(user));
  window.dispatchEvent(new Event(SITE_SHARING_CHANGED));
  await request('PATCH',{enabled:false,consentId},true);
}
export const publishSitePresence=(sharing:SiteSharing,position:{coords:Pick<GeolocationCoordinates,'latitude'|'longitude'|'accuracy'>;timestamp:number})=>request('POST',{
  consentId:sharing.consentId,latitude:position.coords.latitude,longitude:position.coords.longitude,
  accuracy:position.coords.accuracy,capturedAt:new Date(position.timestamp).toISOString(),form:[...activeForms.values()].filter(entry=>entry.user===signedInBidUserId()).slice(-1)[0]?.form||null,
});
